// AUTO-GENERATED from shared/legal/markdown.ts — do not edit. Run npm run sync:shared.
/**
 * SOURCE OF TRUTH — `shared/legal/markdown.ts`. Do not edit the generated
 * copies in `src/types/legal/shared/`, `web/src/legal/shared/` or
 * `marketing_web/src/legal/shared/`; edit this file and run
 * `npm run sync:shared`.
 *
 * FNDTN-33: the one parser for "legal markdown", the body format of the
 * hosted Terms of Service and Privacy Policy. The app, the admin preview and
 * the marketing site all render from this output, so the three never
 * disagree about what a document says.
 *
 * The format is a deliberately tiny subset of markdown:
 *
 *   - Blocks are separated by one or more blank lines.
 *   - `## Heading` is a section heading, `### Heading` a subsection. There is
 *     no level 1 (the title is its own field), so `# x` is a plain paragraph.
 *   - A block whose every line starts with `- ` is a bullet list, one item
 *     per line.
 *   - Any other block is a paragraph. A single newline inside a paragraph is
 *     a hard line break, kept as `\n` inside the inline text.
 *   - Inline: `**bold**` and `[text](url)`, where url is `https://…`,
 *     `http://…`, `mailto:…` or a site-relative path such as `/privacy`.
 *     Any other link stays plain text, brackets and all.
 *   - A paragraph that is wholly one `**…**` span is a notice (the app's
 *     `BoldParagraph`, the website's `p.uppercase-notice`).
 *
 * Unknown syntax is plain text and the parser never throws: a typo in a
 * published document must show up as odd text, never as a blank screen.
 * Plain data and pure functions only: no imports.
 */

export type LegalInline = {text: string; bold?: boolean; href?: string};

export type LegalBlock =
  | {type: 'heading'; level: 2 | 3; text: string}
  | {type: 'paragraph'; inlines: LegalInline[]; notice: boolean}
  | {type: 'list'; items: LegalInline[][]};

const HEADING = /^(#{2,3})[ \t]+(\S.*)$/;
const LINK = /\[([^\]\n]+)\]\(([^()\s]+)\)/;
const BULLET = '- ';
const BOLD = '**';

/**
 * Link targets the renderers may open. A site-relative path must not start
 * with `//`, which a browser reads as another host. No href may contain a
 * backslash at all: browsers turn `\` into `/`, so `/\evil.com` would open
 * as `//evil.com`, off the site.
 */
function isAllowedHref(url: string): boolean {
  if (url.includes('\\')) return false;
  return (
    /^https?:\/\/\S+$/i.test(url) ||
    /^mailto:\S+$/i.test(url) ||
    /^\/(?![\/\\])\S*$/.test(url)
  );
}

/** Appends an inline, merging it into the previous one when they look the same. */
function push(out: LegalInline[], inline: LegalInline): void {
  if (inline.text === '') return;
  const last = out[out.length - 1];
  if (last && !last.href && !inline.href && !!last.bold === !!inline.bold) {
    last.text += inline.text;
    return;
  }
  out.push(inline);
}

/** Splits one run of same-weight text into plain text and links. */
function pushWithLinks(out: LegalInline[], text: string, bold: boolean): void {
  let rest = text;
  while (rest.length > 0) {
    const match = LINK.exec(rest);
    if (!match) break;
    const [whole, label, url] = match;
    const end = match.index + whole.length;
    if (isAllowedHref(url)) {
      push(out, bold ? {text: rest.slice(0, match.index), bold} : {text: rest.slice(0, match.index)});
      push(out, bold ? {text: label, bold, href: url} : {text: label, href: url});
    } else {
      // Not a link we open: the whole `[label](url)` stays as written.
      push(out, bold ? {text: rest.slice(0, end), bold} : {text: rest.slice(0, end)});
    }
    rest = rest.slice(end);
  }
  push(out, bold ? {text: rest, bold} : {text: rest});
}

/** `**bold**` and `[text](url)`. An unclosed `**` is plain text. */
function parseInlines(text: string): LegalInline[] {
  const out: LegalInline[] = [];
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf(BOLD, i);
    const close = open === -1 ? -1 : text.indexOf(BOLD, open + BOLD.length + 1);
    if (open === -1 || close === -1) {
      pushWithLinks(out, text.slice(i), false);
      break;
    }
    pushWithLinks(out, text.slice(i, open), false);
    pushWithLinks(out, text.slice(open + BOLD.length, close), true);
    i = close + BOLD.length;
  }
  return out;
}

function isNotice(text: string): boolean {
  if (text.length <= BOLD.length * 2) return false;
  if (!text.startsWith(BOLD) || !text.endsWith(BOLD)) return false;
  const inner = text.slice(BOLD.length, -BOLD.length);
  return inner.trim().length > 0 && !inner.includes(BOLD);
}

/** One blank-line-separated block (lines already trimmed, none empty). */
function pushBlock(blocks: LegalBlock[], lines: string[]): void {
  if (lines.length === 0) return;
  if (lines.every((line) => line.startsWith(BULLET))) {
    blocks.push({
      type: 'list',
      items: lines.map((line) => parseInlines(line.slice(BULLET.length).trim())),
    });
    return;
  }
  const text = lines.join('\n');
  blocks.push({type: 'paragraph', inlines: parseInlines(text), notice: isNotice(text)});
}

/**
 * Parses a legal markdown body into blocks. Never throws; a non-string or
 * empty source gives `[]`. `\r\n` is read as `\n`, and every line is trimmed,
 * so stray indentation or trailing spaces never change the output.
 *
 * A heading line always stands alone: if one sits at the top or in the middle
 * of a block, the lines around it become blocks of their own.
 */
export function parseLegalMarkdown(source: string): LegalBlock[] {
  const blocks: LegalBlock[] = [];
  if (typeof source !== 'string' || source.length === 0) return blocks;

  let lines: string[] = [];
  for (const raw of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (line === '') {
      pushBlock(blocks, lines);
      lines = [];
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading) {
      pushBlock(blocks, lines);
      lines = [];
      blocks.push({
        type: 'heading',
        level: heading[1].length === 2 ? 2 : 3,
        text: heading[2].trim(),
      });
      continue;
    }
    lines.push(line);
  }
  pushBlock(blocks, lines);
  return blocks;
}
